import { OpenFeatureEventEmitter, ProviderEvents, FlagNotFoundError, TypeMismatchError, ProviderFatalError } from '@openfeature/web-sdk';
import { UnleashClient } from 'unleash-proxy-client';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class UnleashWebProvider {
    constructor(config, logger) {
        this.metadata = {
            name: UnleashWebProvider.name,
        };
        this.events = new OpenFeatureEventEmitter();
        this.runsOn = 'client';
        this._config = config;
        this._logger = logger;
        this._client = new UnleashClient(config);
    }
    get unleashClient() {
        return this._client;
    }
    initialize() {
        return __awaiter(this, undefined, undefined, function* () {
            var _a;
            yield this.initializeClient();
            (_a = this._logger) === null || _a === undefined ? undefined : _a.debug('UnleashWebProvider initialized');
        });
    }
    initializeClient() {
        return __awaiter(this, undefined, undefined, function* () {
            var _a;
            try {
                this.registerEventListeners();
                yield ((_a = this._client) === null || _a === void 0 ? void 0 : _a.start());
            }
            catch (e) {
                throw new ProviderFatalError(getErrorMessage(e));
            }
        });
    }
    registerEventListeners() {
        var _a, _b, _c, _d;
        (_a = this._client) === null || _a === undefined ? undefined : _a.on('ready', () => {
            var _a;
            (_a = this._logger) === null || _a === undefined ? undefined : _a.debug('Unleash ready event received');
            this.events.emit(ProviderEvents.Ready, {
                message: 'Ready',
            });
        });
        (_b = this._client) === null || _b === undefined ? undefined : _b.on('update', () => {
            var _a;
            (_a = this._logger) === null || _a === undefined ? undefined : _a.debug('Unleash update event received');
            this.events.emit(ProviderEvents.ConfigurationChanged, {
                message: 'Flags changed',
            });
        });
        (_c = this._client) === null || _c === undefined ? undefined : _c.on('error', () => {
            var _a;
            (_a = this._logger) === null || _a === undefined ? undefined : _a.debug('Unleash error event received');
            this.events.emit(ProviderEvents.Error, {
                message: 'Error',
            });
        });
        (_d = this._client) === null || _d === undefined ? undefined : _d.on('recovered', () => {
            var _a;
            (_a = this._logger) === null || _a === undefined ? undefined : _a.debug('Unleash recovered event received');
            this.events.emit(ProviderEvents.Ready, {
                message: 'Recovered',
            });
        });
    }
    onContextChange(_oldContext, newContext) {
        return __awaiter(this, undefined, undefined, function* () {
            var _a, _b;
            const unleashContext = new Map();
            const properties = new Map();
            Object.keys(newContext).forEach((key) => {
                switch (key) {
                    case 'appName':
                    case 'userId':
                    case 'environment':
                    case 'remoteAddress':
                    case 'sessionId':
                    case 'currentTime':
                        unleashContext.set(key, newContext[key]);
                        break;
                    default:
                        properties.set(key, newContext[key]);
                        break;
                }
            });
            if (properties.size > 0) {
                unleashContext.set('properties', Object.fromEntries(properties));
            }
            yield ((_a = this._client) === null || _a === undefined ? undefined : _a.updateContext(Object.fromEntries(unleashContext)));
            (_b = this._logger) === null || _b === undefined ? undefined : _b.debug('Unleash context updated');
        });
    }
    onClose() {
        return __awaiter(this, undefined, undefined, function* () {
            var _a, _b;
            (_a = this._logger) === null || _a === undefined ? undefined : _a.debug('closing Unleash client');
            (_b = this._client) === null || _b === undefined ? undefined : _b.stop();
        });
    }
    resolveBooleanEvaluation(flagKey) {
        var _a;
        const resp = (_a = this._client) === null || _a === undefined ? undefined : _a.isEnabled(flagKey);
        if (typeof resp === 'undefined') {
            throw new FlagNotFoundError();
        }
        return {
            value: resp,
        };
    }
    resolveStringEvaluation(flagKey, defaultValue) {
        return this.evaluate(flagKey, defaultValue, 'string');
    }
    resolveNumberEvaluation(flagKey, defaultValue) {
        return this.evaluate(flagKey, defaultValue, 'number');
    }
    resolveObjectEvaluation(flagKey, defaultValue) {
        return this.evaluate(flagKey, defaultValue, 'object');
    }
    throwTypeMismatchError(variant, variantType, flagType) {
        throw new TypeMismatchError(`Type of requested variant ${variant} is of type ${variantType} but requested flag type of ${flagType}`);
    }
    evaluate(flagKey, defaultValue, flagType) {
        var _a, _b, _c;
        const evaluatedVariant = (_a = this._client) === null || _a === undefined ? undefined : _a.getVariant(flagKey);
        let value;
        let variant;
        if (typeof evaluatedVariant === 'undefined') {
            throw new FlagNotFoundError();
        }
        if (evaluatedVariant.name === 'disabled' || typeof evaluatedVariant.payload === 'undefined') {
            value = defaultValue;
        }
        else {
            variant = evaluatedVariant.name;
            value = (_b = evaluatedVariant.payload) === null || _b === undefined ? undefined : _b.value;
            const variantType = (_c = evaluatedVariant.payload) === null || _c === undefined ? undefined : _c.type;
            if (flagType === 'string' && flagType !== variantType) {
                this.throwTypeMismatchError(variant, variantType, flagType);
            }
            if (flagType === 'number') {
                const numberValue = parseFloat(value);
                if (flagType !== variantType || isNaN(numberValue)) {
                    this.throwTypeMismatchError(variant, variantType, flagType);
                }
                value = numberValue;
            }
            if (flagType === 'object') {
                if (variantType !== 'json' && variantType !== 'csv') {
                    this.throwTypeMismatchError(variant, variantType, flagType);
                }
            }
        }
        return {
            variant: variant,
            value: value,
        };
    }
}
function getErrorMessage(error) {
    if (error instanceof Error) {
        return error.message;
    }
    return String(error);
}

export { UnleashWebProvider };
